import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Button,
  Paper,
  TextField,
  Typography,
  Backdrop,
  CircularProgress,
  Snackbar,
  IconButton,
  TableContainer,
  Table,
  TableRow,
  TableCell,
  TableBody,
  TableHead,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  Tooltip
} from "@material-ui/core";
import { Close, FileCopyOutlined, DeleteOutline, Add, ShareOutlined } from "@material-ui/icons";
import ShareEarnModal from "../../components/share-earn-modal/ShareEarnModal";
import { makeStyles } from "@material-ui/core/styles";
import moment from "moment";
import isEmpty from "lodash/isEmpty";
import Menu from "../menu/menu";
import CustomDatePicker from "../../components/custom-date-picker/custom-date-picker";

import {
  fetchQlink,
  publishQlink,
  revokeQlink,
  resetQlinkPublish,
  resetQlinkRevoke,
} from "../../actions";

import "./qlink.scss";

export default function QlinkPage() {
  const classes = useStyles();
  const dispatch = useDispatch();

  const [snackbarType, setSnackbarType] = useState(null);
  const [expirationDate, setExpirationDate] = useState(null);
  const [searchTerm, setSearchTerm] = useState(""); // Added for search
  const [statusFilter, setStatusFilter] = useState("All"); // Added for status filter
  const [generatedQLinks, setGeneratedQLinks] = useState([]);
  const [copySuccessId, setCopySuccessId] = useState(null);
  const [shareModal, setShareModal] = useState(false);
  const [questionIds, setQuestionIds] = useState([""]);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");

  const fetchedQlinkObj = useSelector((state) => state.fetchQlink);
  const publishedQlinkObj = useSelector((state) => state.publishQlink);
  const revokedObj = useSelector((state) => state.revokeQlink);

  const qLinkTableHeader = {
    questionIds: "Question IDs",
    title: "Title",
    description: "Description",
    url: "URL",
    previewUrl: "Preview URL",
    expiresAt: "Expires At",
    createdAt: "Created At",
    status: "Status"
  };

  const handlePublish = () => {
    const formattedDate = moment(expirationDate).format('YYYY-MM-DD[T]HH:mm:ss');
    const qlinkData = {
      questionIds: questionIds,
      expirationTime: formattedDate,
      title,
      description
    };
    dispatch(publishQlink({ qlinkData }));

  };

  const handleQlinkRevoke = (qLinkID) => {
    dispatch(revokeQlink({ qLinkID }));
  };

  const handleShareClick = () => {
    setShareModal(true);
  };

  const handleCopyClick = async (textToCopy, rowId) => {
    await navigator.clipboard.writeText(textToCopy);
    setCopySuccessId(rowId);
    setTimeout(() => {
      setCopySuccessId(null);
    }, 1000);
  };

  const handleAddQuestionId = () => {
    setQuestionIds([...questionIds, ""]);
  };

  const handleRemoveQuestionId = (index) => {
    const updated = questionIds.filter((_, idx) => idx !== index);
    setQuestionIds(updated);
  };

  const handleChangeQuestionId = (index, value) => {
    const updated = [...questionIds];
    updated[index] = value;
    setQuestionIds(updated);
  };


  useEffect(() => {
    if (!isEmpty(publishedQlinkObj) && !publishedQlinkObj?.loading) {
      setSnackbarType("publish");
      if (publishedQlinkObj?.publishedQlinkObj) {
        dispatch(fetchQlink());

        setTitle("");
        setDescription("");
        setExpirationDate(null);
        setQuestionIds([""]);
      }
    }
  }, [publishedQlinkObj]);

  useEffect(() => {
    // if (!isEmpty(fetchedQlinkObj) && !fetchedQlinkObj?.loading) {
    //   setSnackbarType("fetchQlink");
    // }
    setGeneratedQLinks(fetchedQlinkObj?.qlinkObj ?? []);
  }, [fetchedQlinkObj]);

  useEffect(() => {
    if (!isEmpty(revokedObj) && !revokedObj?.loading) {
      setSnackbarType("revoke");
      if (revokedObj?.revokedQlinkObj) {
        dispatch(fetchQlink());
      }
    }
  }, [revokedObj]);

  useEffect(() => {
    if (generatedQLinks?.length) {
      setQuestionIds([""])
      setExpirationDate(null);
    }
  }, [generatedQLinks]);

  useEffect(() => {
    dispatch(fetchQlink());
    return () => {
      dispatch(resetQlinkPublish());
      dispatch(resetQlinkRevoke());
    };
  }, [dispatch]);

  const filteredQLinks = generatedQLinks.filter((row) => {
    const matchesSearch =
      row?.questionIds?.some((id) =>
        id.toLowerCase().includes(searchTerm.toLowerCase())
      ) ||
      row?.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      row?.description?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesStatus =
      statusFilter && statusFilter !== "All"
        ? row?.status === statusFilter
        : true;

    return matchesSearch && matchesStatus;
  });

  return (
    <>
      <Backdrop className={classes.backdrop} open={!!publishedQlinkObj?.loading}>
        <CircularProgress color="inherit" />
      </Backdrop>
      <Snackbar
        key={snackbarType}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        autoHideDuration={3000}
        open={Boolean(snackbarType)}
        onClose={() => setSnackbarType(null)}
      >
        <Box
          className={`${snackbarType === "publish"
            ? !publishedQlinkObj?.loading && !publishedQlinkObj?.error
              ? "success-alert"
              : "success-warn"
            : snackbarType === "revoke"
              ? !revokedObj?.loading && !revokedObj?.error
                ? "success-alert"
                : "success-warn"
              : snackbarType === "fetchQlink"
                ? !fetchedQlinkObj?.loading && !fetchedQlinkObj?.error
                  ? "success-alert"
                  : "success-warn"
                : ""
            } qlink-snackbar`}
        >
          {snackbarType === "publish" &&
            (!publishedQlinkObj?.loading && !publishedQlinkObj?.error
              ? `Qlink URL published successfully`
              : `Failed to publish url - ${publishedQlinkObj?.errorMsg}. Please try again`)}

          {snackbarType === "revoke" &&
            (!revokedObj?.loading && !revokedObj?.error
              ? `Qlink URL revoked successfully`
              : `Failed to revoke url - ${revokedObj?.errorMsg}.`)}

          {snackbarType === "fetchQlink" &&
            (!fetchedQlinkObj?.loading && !fetchedQlinkObj?.error
              ? `Qlinks fetched successfully`
              : `Failed to fetch Qlinks - ${fetchedQlinkObj?.errorMsg}.`)}

          <IconButton
            size="small"
            aria-label="close"
            color="inherit"
            onClick={() => setSnackbarType(null)}
          >
            <Close fontSize="small" />
          </IconButton>
        </Box>
      </Snackbar>

      <Box className={`${classes.container} qlink-container`}>
        <Menu />
        <Box className="qlink-wrapper">
          <Box className="qlink-paper-container-heading">
            <Typography variant="h4">Qlink</Typography>
          </Box>
          <Paper elevation={2} className="qlink-paper-container">
            <Box
              style={{
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                borderRadius: 8, padding: '12px 16px', marginBottom: 16,
                display: 'flex', alignItems: 'center', justifyContent: 'space-between'
              }}
            >
              <Box>
                <Typography variant="subtitle1" style={{ color: '#fff', fontWeight: 600 }}>
                  Share & Earn Free Pro
                </Typography>
                <Typography variant="caption" style={{ color: 'rgba(255,255,255,0.85)' }}>
                  Share your QLink and earn free Pro when someone joins and finishes their first quiz
                </Typography>
              </Box>
              <Button
                variant="contained"
                style={{ background: '#fff', color: '#764ba2', fontWeight: 600, whiteSpace: 'nowrap', marginLeft: 16 }}
                startIcon={<ShareOutlined />}
                onClick={() => handleShareClick()}
              >
                Share & Earn
              </Button>
            </Box>

            <Typography variant="h6" gutterBottom>
              Create new question URL
            </Typography>
            <Paper elevation={2} className="qlink-publish-container">
              <Box display="flex" sx={{ flexDirection: "column" }}>
                <TextField
                  label="Title"
                  className="qlink-title-input"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  margin="normal"
                  fullWidth
                  variant="outlined"
                  required
                />

                <TextField
                  label="Description"
                  className="qlink-desc-input"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  margin="normal"
                  fullWidth
                  multiline
                  rows={3}
                  variant="outlined"
                  required
                />
              </Box>
              <Box sx={{
                display: "flex",
                width: "100%",
                flexWrap: "wrap",
                gap: "0px 16px"
              }}>
                {questionIds.map((id, index) => (
                  <Box display="flex" alignItems="center" key={index} gap={1} >
                    <TextField
                      className="qlink-question-id-input"
                      label={`Question ID ${index + 1}`}
                      value={id}
                      onChange={(e) => handleChangeQuestionId(index, e.target.value)}
                      margin="normal"
                      variant="outlined"
                      required
                    />
                    {questionIds.length > 1 && (
                      <IconButton
                        size="small"
                        aria-label="copyToClipboard"
                        color="inherit"
                        onClick={() => handleRemoveQuestionId(index)}
                      >
                        <DeleteOutline fontSize="small" />
                      </IconButton>
                    )}
                    {index === questionIds.length - 1 && (
                      <IconButton onClick={handleAddQuestionId}>
                        <Add fontSize="small" />
                      </IconButton>
                    )}
                  </Box>
                ))}
              </Box>
              <Box sx={{ maxWidth: "250px" }}>
                <CustomDatePicker
                  className="qlink-date-picker"
                  label="Expiration Date *"
                  value={expirationDate}
                  onChange={setExpirationDate}
                  required
                />
              </Box>
              <Box display="flex" marginTop={2}>
                <Button
                  disabled={questionIds.some(id => !id.trim()) || !expirationDate || !title || !description}
                  variant="contained"
                  color="primary"
                  onClick={handlePublish}
                >
                  Publish URL
                </Button>
              </Box>
            </Paper>



            <Typography variant="h6" gutterBottom>
              Generated Qlinks
            </Typography>

            {/* Filter Controls */}
            <Box sx={{ display: "flex", gap: 10, mb: 2 }} className="qlink-filter-container">
              <TextField
                className="qlink-qsid-filter-input"
                label="Search by Question ID, Title or Description"
                variant="outlined"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}

              />
              <FormControl variant="outlined" >
                <InputLabel>Status</InputLabel>
                <Select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  label="Status"
                  fullWidth
                >
                  <MenuItem value="All">All</MenuItem>
                  <MenuItem value="ACTIVE">Active</MenuItem>
                  <MenuItem value="EXPIRED">Expired</MenuItem>
                  <MenuItem value="REVOKED">Revoked</MenuItem>
                </Select>
              </FormControl>
            </Box>
            {filteredQLinks.length !== 0 && (
              <Paper>
                <TableContainer component={Paper}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        {Object.keys(qLinkTableHeader)?.map((headerName, idx) => (
                          <TableCell key={idx}>{qLinkTableHeader?.[headerName]}</TableCell>
                        ))}
                        <TableCell></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredQLinks.map((row, index) => (
                        <TableRow
                          key={index}
                        >
                          {Object.keys(qLinkTableHeader)?.map((headerName, idx) => (
                            <TableCell
                              className={`${classes.cell} ${headerName === 'status'
                                ? row?.status === "REVOKED"
                                  ? "revoked-qlink-row"
                                  : row?.status === "EXPIRED"
                                    ? "expired-qlink-row"
                                    : "active-qlink-row"
                                : ""
                                }`}
                              key={`${index}-${idx}`}
                            >
                              {['url', 'previewUrl'].includes(headerName) ? (
                                <Tooltip title={row[headerName]}>
                                  <Box
                                    sx={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      maxWidth: 200,
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      whiteSpace: 'nowrap',
                                    }}
                                  >
                                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                      {row[headerName]}
                                    </span>
                                    <Tooltip title={copySuccessId === `${row.id}-${headerName}` ? 'Copied!' : 'Copy'} open={copySuccessId === `${row.id}-${headerName}`}>
                                      <IconButton
                                        size="small"
                                        aria-label="copy"
                                        onClick={() => handleCopyClick(row[headerName], `${row.id}-${headerName}`)}
                                      >
                                        <FileCopyOutlined fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                  </Box>
                                </Tooltip>
                              ) : headerName === 'expiresAt' || headerName === 'createdAt' ? (
                                <>{moment(row?.[headerName]).format('YYYY-MM-DD')}</>
                              ) : headerName === 'questionIds' ? (
                                <Box component="ul" sx={{ pl: 2, mb: 0 }}>
                                  {row?.[headerName]?.map((id, i) => (
                                    <li key={i}>{id}</li>
                                  ))}
                                </Box>
                              ) : (
                                <>{row?.[headerName]}</>
                              )}
                            </TableCell>
                          ))}

                          <TableCell>
                            <Box display="flex" style={{ gap: 8 }}>
                              {row?.status !== "REVOKED" && row?.status !== "EXPIRED" && (
                                <Button
                                  color="primary"
                                  variant="contained"
                                  onClick={() => handleQlinkRevoke(row?.id)}
                                >
                                  Revoke
                                </Button>
                              )}
                              {row?.status === "ACTIVE" && (
                                <Button
                                  variant="outlined"
                                  color="secondary"
                                  startIcon={<ShareOutlined />}
                                  onClick={() => handleShareClick()}
                                >
                                  Share
                                </Button>
                              )}
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            )}
          </Paper>
        </Box>
      </Box>

      <ShareEarnModal
        open={shareModal}
        onClose={() => setShareModal(false)}
      />
    </>
  );
}

const useStyles = makeStyles((theme) => ({
  container: {
    display: "flex",
    height: "100vh",
    width: "100vw",
  },
  backdrop: {
    zIndex: theme.zIndex.drawer + 1,
    color: "#fff",
  },
  cell: {
    maxWidth: 400,
    whiteSpace: "normal",
    wordWrap: "break-word",
    overflowWrap: "break-word",
  },
}));
